/**
 * shell/blog-page.ts
 *
 * Progressive enhancement controller for the public /blog/{slug} article page.
 * Sibling of shell/legal-page.ts.
 *
 * Capabilities (all degrade gracefully without JS):
 *   - Reading-progress bar bound to scroll position (`--blog-progress`).
 *   - Sticky table-of-contents active-section tracking via IntersectionObserver.
 *   - Honours `prefers-reduced-motion` for smooth in-page scroll.
 *
 * Imports use `.js` even for `.ts` source files (per AGENTS §10).
 */
import { byId, queryAll } from '../types/dom.js';
// ---------------------------------------------------------------------------
// Reading progress
// ---------------------------------------------------------------------------
function initReadingProgress(root) {
    let ticking = false;
    const update = () => {
        const docHeight = document.documentElement.scrollHeight - window.innerHeight;
        const ratio = docHeight > 0
            ? Math.max(0, Math.min(1, window.scrollY / docHeight))
            : 0;
        root.style.setProperty('--blog-progress', `${(ratio * 100).toFixed(2)}%`);
        ticking = false;
    };
    const onScroll = () => {
        if (ticking)
            return;
        ticking = true;
        window.requestAnimationFrame(update);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    update();
}
function initTocTracking() {
    const links = queryAll('.blog-toc__link');
    if (links.length === 0)
        return;
    const linkByTarget = new Map();
    for (const element of links) {
        const href = element.getAttribute('href') ?? '';
        if (!href.startsWith('#'))
            continue;
        const targetId = decodeURIComponent(href.slice(1));
        if (targetId.length === 0)
            continue;
        linkByTarget.set(targetId, { element, targetId });
    }
    const sections = [];
    for (const [id] of linkByTarget) {
        const el = document.getElementById(id);
        if (el !== null)
            sections.push(el);
    }
    if (sections.length === 0)
        return;
    const setActive = (id) => {
        for (const link of linkByTarget.values()) {
            link.element.classList.toggle('is-active', link.targetId === id);
        }
    };
    if (typeof IntersectionObserver === 'undefined') {
        return;
    }
    const visible = new Map();
    const observer = new IntersectionObserver((entries) => {
        for (const entry of entries) {
            const id = entry.target.id;
            if (id.length === 0)
                continue;
            if (entry.isIntersecting) {
                visible.set(id, entry.intersectionRatio);
            }
            else {
                visible.delete(id);
            }
        }
        // Pick the heading closest to the top of the viewport.
        let bestId = null;
        let bestTop = Infinity;
        for (const id of visible.keys()) {
            const el = document.getElementById(id);
            if (el === null)
                continue;
            const top = el.getBoundingClientRect().top;
            if (top < bestTop && top < window.innerHeight * 0.4) {
                bestTop = top;
                bestId = id;
            }
        }
        setActive(bestId);
    }, {
        rootMargin: '-10% 0px -70% 0px',
        threshold: [0, 0.25, 0.5, 0.75, 1],
    });
    for (const section of sections)
        observer.observe(section);
}
// ---------------------------------------------------------------------------
// Boot
// ---------------------------------------------------------------------------
function init() {
    const root = byId('blogArticle');
    if (root === null)
        return;
    initReadingProgress(root);
    initTocTracking();
}
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
}
else {
    init();
}
//# sourceMappingURL=blog-page.js.map